
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { processPdfFile } from "@/services/api/pdf";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export function PdfApiTest() {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [apiStatus, setApiStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  
  // Check if the API is online when the component mounts
  useEffect(() => {
    const checkApiStatus = async () => {
      try {
        const response = await fetch("https://everest-pdf-api.onrender.com/");
        if (response.ok) {
          setApiStatus('online');
        } else {
          setApiStatus('offline');
        }
      } catch (err) {
        setApiStatus('offline');
      }
    };
    
    checkApiStatus();
  }, []);
  
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await processPdfFile(file);
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      setResult(null);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>API Connection Test</span>
          {apiStatus === 'checking' && (
            <span className="flex items-center text-sm font-normal text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Checking API status...
            </span>
          )}
          {apiStatus === 'online' && (
            <span className="flex items-center text-sm font-normal text-green-600">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              API Online
            </span>
          )}
          {apiStatus === 'offline' && (
            <span className="flex items-center text-sm font-normal text-destructive">
              <AlertCircle className="h-4 w-4 mr-2" />
              API Offline
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {apiStatus === 'offline' && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              The PDF processing API is currently offline or inaccessible. Please try again later.
            </AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-4">
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            className="block w-full text-sm text-slate-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-primary file:text-primary-foreground
              hover:file:bg-primary/90"
            disabled={isLoading || apiStatus !== 'online'}
          />
          
          {isLoading && (
            <div className="flex items-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Processing PDF...</span>
            </div>
          )}
          
          {error && (
            <div className="p-4 bg-destructive/10 text-destructive rounded-md">
              <h3 className="font-medium">Error</h3>
              <p className="text-sm">{error}</p>
            </div>
          )}
          
          {result && (
            <div className="p-4 bg-muted rounded-md">
              <h3 className="font-medium">API Response</h3>
              <div className="mt-2 space-y-2">
                <p><span className="font-medium">VIN:</span> {result.vehicle?.vin || 'Not found'}</p>
                <p><span className="font-medium">Vehicle:</span> {result.vehicle?.year} {result.vehicle?.make} {result.vehicle?.model}</p>
                <p><span className="font-medium">Total:</span> ${result.calculated_total?.toFixed(2) || '0.00'}</p>
                <p><span className="font-medium">Line Items:</span> {result.repair_sections?.length || 0}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
